import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, EmptyState } from "@/components/common";
import { api, errMsg, pretty } from "@/lib/api";

const CATS = ["tax", "business_registration", "social_security", "local_government", "workplace_safety", "regulatory", "other"];

function AuthorityDialog({ a, onClose, onDone }) {
  const [f, setF] = useState(a ? { short_name: a.short_name, full_name: a.full_name, category: a.category, description: a.description || "" } : { short_name: "", full_name: "", category: "other", description: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = () => (a ? api.put(`/authorities/${a.id}`, f) : api.post("/authorities", f)).then(() => { toast.success("Authority saved"); onDone(); onClose(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="authority-dialog">
        <DialogHeader><DialogTitle>{a ? `Edit ${a.short_name}` : "New authority"}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <label className="text-sm font-medium">Short name<Input value={f.short_name} onChange={set("short_name")} placeholder="e.g. TRA" data-testid="authority-short-input" /></label>
          <label className="text-sm font-medium">Full name<Input value={f.full_name} onChange={set("full_name")} data-testid="authority-full-input" /></label>
          <label className="text-sm font-medium">Category
            <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}><SelectTrigger data-testid="authority-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{pretty(c)}</SelectItem>)}</SelectContent></Select>
          </label>
          <label className="text-sm font-medium">Description (optional)<Textarea value={f.description} onChange={set("description")} data-testid="authority-desc-input" /></label>
        </div>
        <DialogFooter><Button onClick={save} data-testid="authority-save-btn">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UsageDialog({ a, onClose }) {
  const [u, setU] = useState(null);
  useEffect(() => { api.get(`/authorities/${a.id}/usage`).then((r) => setU(r.data)); }, [a.id]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="authority-usage-dialog">
        <DialogHeader><DialogTitle>Where {a.short_name} is used</DialogTitle></DialogHeader>
        {!u ? <p className="text-sm text-muted-foreground">Loading…</p> : <>
          <p className="text-sm">{u.templates.length} template(s) · {u.tasks} task cycle(s)</p>
          <ul className="max-h-72 space-y-1 overflow-y-auto text-sm">{u.templates.map((t) => <li key={t.id} className="rounded border px-3 py-2">{t.name}{t.is_custom ? " · company internal" : ""}</li>)}</ul>
        </>}
      </DialogContent>
    </Dialog>
  );
}

export default function Authorities() {
  const [rows, setRows] = useState(null);
  const [review, setReview] = useState([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(undefined);
  const [usage, setUsage] = useState(null);
  const load = () => {
    api.get("/authorities", { params: q ? { q } : {} }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/authorities/review").then((r) => setReview(r.data)).catch(() => {});
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q]); // eslint-disable-line
  const toggle = (a) => api.post(`/authorities/${a.id}/active`, { is_active: !a.is_active }).then(() => { toast.success(a.is_active ? "Deactivated — existing links are kept" : "Activated"); load(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div data-testid="authorities-page">
      <PageHeader title="Authorities" subtitle="One master list of regulators used by templates, obligations and filters">
        <Button onClick={() => setEdit(null)} data-testid="authority-new-btn"><Plus className="mr-2 h-4 w-4" />New authority</Button>
      </PageHeader>
      {review.length > 0 && (
        <div className="mb-5 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:bg-amber-500/10 dark:text-amber-200" data-testid="authority-review-list">
          <div className="mb-2 flex items-center gap-2 font-semibold"><AlertTriangle className="h-4 w-4" />{review.length} template(s) have an authority name that could not be matched safely</div>
          <ul className="space-y-1">{review.map((t) => <li key={t.id}>“{t.authority_legacy}” on <b>{t.name}</b></li>)}</ul>
          <p className="mt-2">Open <Link to="/templates" className="font-semibold underline">Templates</Link>, edit each one and choose the correct authority, or create it here first.</p>
        </div>
      )}
      <div className="relative mb-4 max-w-sm"><Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" /><Input className="pl-9" placeholder="Search short or full name" value={q} onChange={(e) => setQ(e.target.value)} data-testid="authority-search-input" /></div>
      {!rows ? <EmptyState title="Loading…" /> : !rows.length ? <EmptyState title="No authorities found" testId="authorities-empty" /> : (
        <div className="overflow-x-auto rounded-lg border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground"><tr><th className="px-4 py-3">Short name</th><th className="px-4 py-3">Full name</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Active</th><th className="px-4 py-3" /></tr></thead>
            <tbody>{rows.map((a) => (
              <tr key={a.id} className="border-t" data-testid={`authority-row-${a.short_name}`}>
                <td className="px-4 py-3 font-bold">{a.short_name}</td><td className="px-4 py-3">{a.full_name}</td><td className="px-4 py-3">{pretty(a.category)}</td>
                <td className="px-4 py-3"><Switch checked={a.is_active} onCheckedChange={() => toggle(a)} data-testid={`authority-active-${a.short_name}`} /></td>
                <td className="whitespace-nowrap px-4 py-3 text-right"><Button size="sm" variant="ghost" onClick={() => setUsage(a)} data-testid={`authority-usage-${a.short_name}`}>Usage</Button><Button size="sm" variant="outline" onClick={() => setEdit(a)} data-testid={`authority-edit-${a.short_name}`}>Edit</Button></td>
              </tr>))}</tbody>
          </table>
        </div>
      )}
      {edit !== undefined && <AuthorityDialog a={edit} onClose={() => setEdit(undefined)} onDone={load} />}
      {usage && <UsageDialog a={usage} onClose={() => setUsage(null)} />}
    </div>
  );
}
